import { useState, useCallback, useRef, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SkipForward, ChevronLeft, ChevronRight, AlertTriangle, RotateCcw, Navigation } from 'lucide-react';
import { GLOBAL_UI } from '@/config/missionContent';

/* ─── Error Boundary — catches stage crashes gracefully ─── */
class StageErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleSkip = () => {
    this.setState({ hasError: false, error: null });
    this.props.onSkip?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1.5px solid rgba(239,68,68,0.3)' }}>
              <AlertTriangle size={28} style={{ color: '#ef4444' }} />
            </div>
            <h2 className="text-xl font-orbitron font-bold tracking-[0.15em] mb-2"
              style={{ color: '#ef4444' }}>{GLOBAL_UI.errorBoundary.heading}</h2>
            <p className="text-sm font-terminal tracking-wider mb-1"
              style={{ color: 'rgba(239,68,68,0.5)' }}>
              {GLOBAL_UI.errorBoundary.message}
            </p>
            <p className="text-xs font-terminal tracking-wider mb-8"
              style={{ color: 'rgba(251,191,36,0.4)' }}>
              {this.state.error?.message || GLOBAL_UI.errorBoundary.unknownError}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-orbitron text-xs tracking-wider"
                style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.3)', color: '#f9a8d4' }}>
                <RotateCcw size={12} /> {GLOBAL_UI.errorBoundary.retryButton}
              </button>
              <button onClick={this.handleSkip}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-orbitron text-xs tracking-wider"
                style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80' }}>
                <SkipForward size={12} /> {GLOBAL_UI.errorBoundary.skipButton}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import PasswordGate from '@/components/stages/PasswordGate';
import CinematicIntro from '@/components/stages/CinematicIntro';
import MissionBriefing from '@/components/stages/MissionBriefing';
import Puzzle1WordScramble from '@/components/stages/Puzzle1WordScramble';
import ShootingGallery from '@/components/stages/ShootingGallery';
import Puzzle2SafeCracker from '@/components/stages/Puzzle2SafeCracker';
import HerbMixing from '@/components/stages/HerbMixing';
import Puzzle3MemoryMatch from '@/components/stages/Puzzle3MemoryMatch';
import LaserGrid from '@/components/stages/LaserGrid';
import Puzzle4CipherDecode from '@/components/stages/Puzzle4CipherDecode';
import Lockpick from '@/components/stages/Lockpick';
import Puzzle5WirePuzzle from '@/components/stages/Puzzle5WirePuzzle';
import RadioTuning from '@/components/stages/RadioTuning';
import MusicBoxMelody from '@/components/stages/MusicBoxMelody';
import Puzzle6FinalLock from '@/components/stages/Puzzle6FinalLock';
import EscapeSequence from '@/components/stages/EscapeSequence';
import GrandReveal from '@/components/stages/GrandReveal';
import Inventory from '@/components/Inventory';
import AudioManager from '@/components/AudioManager';
import ValentineChapter from '@/components/valentine/ValentineChapter';

const STAGE_COMPONENTS = [
  PasswordGate,       // 0
  CinematicIntro,     // 1
  MissionBriefing,    // 2
  Puzzle1WordScramble,// 3 - Checkpoint 01: Data Recovery
  ShootingGallery,    // 4 - Checkpoint 02: Firing Range
  Puzzle2SafeCracker, // 5 - Checkpoint 03: Vault Access
  HerbMixing,         // 6 - Checkpoint 04: Chemical Lab
  Puzzle3MemoryMatch, // 7 - Checkpoint 05: Security Clearance
  LaserGrid,          // 8 - Checkpoint 06: Laser Corridor
  Puzzle4CipherDecode,// 9 - Checkpoint 07: Intel Decryption
  Lockpick,           // 10 - Checkpoint 08: Lockpick
  Puzzle5WirePuzzle,  // 11 - Checkpoint 09: Power Grid
  RadioTuning,        // 12 - Checkpoint 10: Radio Contact
  MusicBoxMelody,     // 13 - Checkpoint 11: Music Box
  Puzzle6FinalLock,   // 14 - Final Checkpoint: Main Vault
  EscapeSequence,     // 15 - Escape: System Breach
  GrandReveal,        // 16
];

const getPhase = (stage) => {
  if (stage <= 2) return 'umbrella';
  if (stage <= 11) return 'mission';
  if (stage >= 17) return 'valentine';
  return 'romantic';
};

export default function StageManager() {
  const [currentStage, setCurrentStage] = useState(0);
  const [stageVisit, setStageVisit] = useState(0); // bumped on every stage change to force remount
  const [inventory, setInventory] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwValue, setPwValue] = useState('');
  const [pwError, setPwError] = useState(false);
  const navUnlockedRef = useRef(false);
  const pwInputRef = useRef(null);

  const toggleNav = useCallback(() => {
    if (showNav) {
      setShowNav(false);
      return;
    }
    if (navUnlockedRef.current) {
      setShowNav(true);
      return;
    }
    setPwValue('');
    setPwError(false);
    setShowPwModal(true);
  }, [showNav]);

  const handlePwSubmit = useCallback(() => {
    if (pwValue === 'Dev') {
      navUnlockedRef.current = true;
      setShowPwModal(false);
      setShowNav(true);
      setPwValue('');
      setPwError(false);
    } else {
      setPwError(true);
      setPwValue('');
      pwInputRef.current?.focus();
    }
  }, [pwValue]);

  const advanceStage = useCallback(() => {
    setCurrentStage(prev => {
      if (prev >= STAGE_COMPONENTS.length) return prev;
      return prev + 1;
    });
    setStageVisit(prev => prev + 1);
  }, []);

  const collectItem = useCallback((item) => {
    setInventory(prev => [...prev, item]);
  }, []);

  const handleAudioToggle = useCallback(() => {
    setAudioEnabled(prev => !prev);
    if (!audioStarted) setAudioStarted(true);
  }, [audioStarted]);

  const pwModal = showPwModal && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      onClick={() => setShowPwModal(false)}>
      <div className="relative w-72 rounded-2xl p-6 text-center"
        style={{ background: 'linear-gradient(145deg, #0a0a0f, #111118)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)' }}>
          <Navigation size={20} style={{ color: '#eab308' }} />
        </div>
        <p className="font-terminal text-xs tracking-wider mb-1" style={{ color: '#eab308' }}>ACCESS REQUIRED</p>
        <p className="text-[10px] mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>Enter password to unlock navigation</p>
        <form onSubmit={(e) => { e.preventDefault(); handlePwSubmit(); }}>
          <input
            ref={pwInputRef}
            type="password"
            value={pwValue}
            onChange={(e) => { setPwValue(e.target.value); setPwError(false); }}
            autoFocus
            placeholder="Password"
            className="w-full px-4 py-2.5 rounded-lg text-sm text-center font-terminal tracking-widest outline-none transition-all"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${pwError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
              color: '#fff',
              caretColor: '#eab308',
            }}
          />
          {pwError && <p className="text-[10px] mt-1.5" style={{ color: '#ef4444' }}>Wrong password</p>}
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => setShowPwModal(false)}
              className="flex-1 py-2 rounded-lg text-xs font-terminal tracking-wider transition-all hover:brightness-125"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
              CANCEL
            </button>
            <button type="submit"
              className="flex-1 py-2 rounded-lg text-xs font-terminal tracking-wider transition-all hover:brightness-125"
              style={{ background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.3)', color: '#eab308', cursor: 'pointer' }}>
              UNLOCK
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const isValentine = currentStage >= STAGE_COMPONENTS.length;
  const StageComponent = STAGE_COMPONENTS[currentStage];
  const phase = getPhase(currentStage);
  const showInventory = currentStage >= 3 && currentStage <= 14;

  if (isValentine) {
    return (
      <div className={`relative min-h-screen phase-${phase}`} data-phase={phase} data-testid="stage-manager">
        <button
          onClick={toggleNav}
          className="fixed top-4 left-4 z-50 w-10 h-10 rounded-full bg-black/60 border flex items-center justify-center hover:border-white/30 transition-all"
          style={{ borderColor: showNav ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)', color: showNav ? '#fff' : '#9ca3af' }}
          title={showNav ? 'Hide navigation' : 'Show navigation'}
        >
          <Navigation size={16} />
        </button>
        {showNav && (
          <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-black/80 border border-yellow-800/40 rounded-full px-2 py-1 backdrop-blur-sm" data-testid="dev-skip">
            <button onClick={() => { setCurrentStage(prev => Math.max(0, prev - 1)); setStageVisit(prev => prev + 1); }} className="w-7 h-7 rounded-full flex items-center justify-center text-yellow-600 hover:bg-yellow-900/20 transition-colors" data-testid="dev-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-terminal text-yellow-700 tracking-wider px-1 min-w-[60px] text-center">{currentStage}/{STAGE_COMPONENTS.length}</span>
            <button onClick={advanceStage} className="w-7 h-7 rounded-full flex items-center justify-center text-yellow-600 hover:bg-yellow-900/20 transition-colors" data-testid="dev-skip-btn">
              <SkipForward size={14} />
            </button>
          </div>
        )}
        <ValentineChapter />
        {pwModal}
      </div>
    );
  }

  return (
    <div
      className={`relative min-h-screen bg-[#020617] overflow-hidden phase-${phase}`}
      data-phase={phase}
      data-testid="stage-manager"
    >
      <div className="scanlines" />
      <div className="vignette" />

      {showInventory && (
        <Inventory items={inventory} />
      )}

      <AudioManager enabled={audioEnabled} started={audioStarted} onToggle={handleAudioToggle} />
      <button
        onClick={toggleNav}
        className="fixed top-16 left-4 z-50 w-10 h-10 rounded-full bg-black/60 border flex items-center justify-center hover:border-white/30 transition-all"
        style={{ borderColor: showNav ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)', color: showNav ? '#fff' : '#9ca3af' }}
        title={showNav ? 'Hide navigation' : 'Show navigation'}
      >
        <Navigation size={16} />
      </button>

      {showNav && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-black/80 border border-yellow-800/40 rounded-full px-2 py-1 backdrop-blur-sm" data-testid="dev-skip">
          <button onClick={() => { setCurrentStage(prev => Math.max(0, prev - 1)); setStageVisit(prev => prev + 1); }} className="w-7 h-7 rounded-full flex items-center justify-center text-yellow-600 hover:bg-yellow-900/20 transition-colors" data-testid="dev-prev">
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs font-terminal text-yellow-700 tracking-wider px-1 min-w-[60px] text-center">{currentStage}/{STAGE_COMPONENTS.length - 1}</span>
          <button onClick={advanceStage} className="w-7 h-7 rounded-full flex items-center justify-center text-yellow-600 hover:bg-yellow-900/20 transition-colors" data-testid="dev-skip-btn">
            <SkipForward size={14} />
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentStage}-${stageVisit}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          {StageComponent && (
            <StageErrorBoundary key={`eb-${currentStage}-${stageVisit}`} onSkip={advanceStage}>
              <StageComponent
                onComplete={advanceStage}
                onItemCollected={collectItem}
                inventory={inventory}
              />
            </StageErrorBoundary>
          )}
        </motion.div>
      </AnimatePresence>
      {pwModal}
    </div>
  );
}
